import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DateRangePicker,
  Divider,
  Input,
  InputSelect,
  Label,
  RadixTable,
  TColumn,
} from '@features/ui';
import Pagination from '@features/ui/components/pagination.tsx';
import { useNavigate } from 'react-router-dom';
import { LeelooUser, UserReq, UserType } from '@src/types/users';
import { userTypeData } from '@features/admin/users/usersData';
import { DateValueType, formatDateByMST } from '@lib/utils';
import queryString from 'query-string';
import { getUsers } from '@src/services/users.ts';
import { TradingPlatForms } from '@src/types';

export const UsersDashboard = () => {
  const [page, setPage] = useState(1);
  const [searchWords, setSearchWords] = useState('');
  const [totalPage, setTotalPage] = useState(1);
  const [expireDate, setExpireDate] = useState<DateValueType>(null);
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [valueEnd, setValueEnd] = useState<DateValueType>(null);
  const [users, setUsers] = useState<LeelooUser[]>([]);

  const navigate = useNavigate();

  const fetchUsers = async (params?: UserReq) => {
    const res = await getUsers(params);

    setUsers(res.data.leeloo_users);
    setTotalPage(res.data.total_pages);
  };

  useEffect(() => {
    fetchUsers().then();
  }, []);

  const handleEditUser = useCallback(
    (id?: number) => {
      if (!id) return;
      navigate(`/admin/users/${id}`);
    },
    [navigate]
  );

  const handleSelectUser = useCallback(
    (value: UserType) => {
      const params = {
        type: value,
      };
      const searchString = queryString.stringify(params);

      navigate({
        pathname: '/admin/users',
        search: searchString,
      });
    },
    [navigate]
  );

  const handleChangePage = useCallback(async (page: number) => {
    setPage(page);
    await fetchUsers({ page });
  }, []);

  const handleFilter = useCallback(async () => {
    await fetchUsers({
      page,
      expire_date_after: expireDate?.startDate
        ? new Date(expireDate.startDate).toISOString()
        : undefined,
      search_words: searchWords,
      start_date: valueStart?.startDate
        ? new Date(valueStart.startDate).toISOString()
        : undefined,
      end_date: valueEnd?.startDate
        ? new Date(valueEnd.startDate).toISOString()
        : undefined,
    });
  }, [
    page,
    searchWords,
    expireDate?.startDate,
    valueStart?.startDate,
    valueEnd?.startDate,
  ]);

  const columns = useMemo(
    (): TColumn<LeelooUser>[] => [
      {
        title: 'User',
        render: (item) => (
          <div className={'flex flex-col gap-1'}>
            <Button
              className={'text-[#3490FF] underline w-fit pl-0 text-xs'}
              variant={'link'}
              onClick={() => handleEditUser(item?.id)}
            >
              {item?.username}
            </Button>
            <p>
              {item?.user?.first_name ?? ''} {item?.user?.last_name ?? ''}
            </p>
          </div>
        ),
      },
      {
        title: 'User Type',
        render: (item) => {
          if (item?.user_type === TradingPlatForms.CQG_DATA_FEED) {
            return (
              <img
                alt="leeloo-logo"
                className={'w-[101px] h-[30px]'}
                src="/assets/logo-tradovate.png"
              />
            );
          }
          if (item?.user_type === TradingPlatForms.RITHMIC_DATA_FEED) {
            return (
              <img
                alt="leeloo-logo"
                className={'w-[101px] h-[30px]'}
                src="/assets/logo-rithmic.png"
              />
            );
          }

          return (
            <img
              alt="leeloo-logo"
              className={'w-[101px] h-[30px]'}
              src="/assets/logo-dxfeed.png"
            />
          );
        },
      },
      {
        title: `Accounts`,
        render: (item) => <p>{item?.leeloo_accounts?.length ?? '-'}</p>,
      },
      {
        title: `Phone Number`,
        render: (item) => <p>{item?.phone_number ?? '-'}</p>,
      },
      {
        title: 'Expires',
        render: (item) => {
          return <p>{formatDateByMST(item?.expire_date) || '-'}</p>;
        },
      },
      {
        title: (
          <div className={'flex justify-center'}>
            <p>Action</p>
          </div>
        ),
        render: (item) => (
          <div className={'flex justify-center'}>
            <Button
              size={'sm'}
              variant={'secondary'}
              onClick={() => handleEditUser(item?.id)}
            >
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [handleEditUser]
  );

  return (
    <div>
      <div className={'flex justify-end mb-5'}>
        <InputSelect
          className={'w-[239px] border-[#C6C6C6]'}
          options={userTypeData}
          placeholder={'Select a report'}
          value={UserType.Users}
          onChange={(value) => handleSelectUser(value as UserType)}
        />
      </div>
      <Card className={'items-end'}>
        <div
          className={
            'flex lg:flex-row flex-col w-full mb-5 gap-6 flex-wrap lg:items-end'
          }
        >
          <div className={'flex flex-col gap-y-2'}>
            <Label className={'text-xs'}>
              Where user expire date is greater than
            </Label>
            <DateRangePicker
              hideHeader
              asSingle={true}
              size={'sm'}
              value={expireDate}
              variant={'outline'}
              onChange={setExpireDate}
            />
          </div>
          <div className={'lg:w-[239px] w-full'}>
            <Input
              placeholder={'type username, name, email,...'}
              textSize={'xs'}
              value={searchWords}
              variant={'secondary'}
              onChange={(event) => setSearchWords(event.target.value)}
            />
          </div>
          <DateRangePicker
            hideHeader
            asSingle={true}
            placeholder={'From Date'}
            size={'sm'}
            value={valueStart}
            variant={'outline'}
            onChange={setValueStart}
          />
          <DateRangePicker
            hideHeader
            asSingle={true}
            placeholder={'To Date'}
            size={'sm'}
            value={valueEnd}
            variant={'outline'}
            onChange={setValueEnd}
          />
          <div className={'mb-1'}>
            <Button size={'sm'} variant={'lime'} onClick={handleFilter}>
              Find
            </Button>
          </div>
        </div>
        <RadixTable columns={columns} data={users} />
        <Divider />
        <Pagination
          className={'mt-6'}
          current={page}
          total={totalPage}
          onChange={handleChangePage}
        />
      </Card>
    </div>
  );
};
